import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  useQueryParams,
  StringParam,
  withDefault,
  NumberParam,
} from 'use-query-params';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../commons/Auth';
import { useAlert } from 'react-alert';
import { useLoading } from '../../commons/Loading';
import CondosPresentational from './CondosPresentational';
import EonCondosDetail from '../EonCondosDetail';
import { Route, Switch, withRouter } from 'react-router-dom';
import BurgerMenu from '../../commons/BurgerMenu';
import Header from '../../commons/Header';
import { ReactComponent as IconBuilding } from '../../images/icn_buildings.svg';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

const EonCondos = ({ profile, match = {}, location }) => {
  const [condos, setCondos] = useState([]);
  const [formattedCSVCondos, setFormattedCSVCondos] = useState([]);
  const [total, setTotal] = useState(0);
  const [{ newToken, newIsAdmin } = {}] = useAuth();
  const [t] = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const alert = useAlert();

  const { pathname = '', state = {} } = location;
  const tab = pathname.split('/').pop();

  const [query, setQuery] = useQueryParams({
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
    offset: NumberParam,
    admin: withDefault(StringParam, ''),
    name: withDefault(StringParam, ''),
    vatCode: withDefault(StringParam, ''),
    address: withDefault(StringParam, ''),
    studioManager: withDefault(StringParam, ''),
    condosActive: withDefault(StringParam, ''),
  });

  const {
    limit,
    offset,
    admin,
    name,
    vatCode,
    address,
    studioManager,
    condosActive,
  } = query;

  useEffect(() => {
    fetchCondos();
  }, [
    limit,
    offset,
    admin,
    name,
    vatCode,
    address,
    studioManager,
    condosActive,
  ]);

  useEffect(() => {
    fetchCondosForExport();
  }, [admin, name, vatCode, address, studioManager, condosActive]);

  const fetchCondos = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/condos/${profile._id}/energy`,
      {
        headers: { Authorization: newToken },
        params: {
          limit,
          offset,
          admin,
          name,
          vatCode,
          address,
          studioManager,
          condosActive,
          forUserView: profile.type === 'EON_ENERGY',
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [], headers = {} } = response;
    setCondos(data.condos);
    setTotal(+headers['x-total-count']);
    // setFormattedCSVCondos(data.formattedCSVCondos);
  };

  const fetchCondosForExport = async () => {
    showLoading();

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/condos/export/${profile._id}/energy`,
      {
        headers: { Authorization: newToken },
        params: {
          limit: 5000,
          offset: 0,
          admin,
          name,
          vatCode,
          address,
          studioManager,
          condosActive,
        },
      }
    );

    hideLoading();

    if (response instanceof Error) {
      return alert.error(t('COMMON:GENERIC_ERROR'));
    }

    const { data = [] } = response;
    setFormattedCSVCondos(data.formattedCSVCondos);
  };

  function onFilter(queryDiff) {
    if (
      (queryDiff.admin && queryDiff.admin !== admin) || // first search
      (admin && queryDiff.admin === '') || // search deleted
      (queryDiff.name && queryDiff.name !== name) || // first search
      (name && queryDiff.name === '') || // search deleted
      (queryDiff.vatCode && queryDiff.vatCode !== vatCode) || // first search
      (vatCode && queryDiff.vatCode === '') || // search deleted
      (queryDiff.address && queryDiff.address !== address) || // first search
      (address && queryDiff.address === '') || // search deleted
      (queryDiff.condosActive && queryDiff.condosActive !== condosActive) || // first search
      (condosActive && queryDiff.condosActive === 'true') || // search deleted
      (queryDiff.studioManager && queryDiff.studioManager !== studioManager) || // first search
      (studioManager && queryDiff.studioManager === '') || // search deleted
      (queryDiff.limit && queryDiff.limit !== limit)
    ) {
      queryDiff.offset = 0; // return to first page
    }

    setQuery(queryDiff);
  }

  return (
    <div className="App">
      <BurgerMenu />

      <div className="corpo">
        <section>
          <Header
            title={t('PAGES:CONDOS')}
            icon={<IconBuilding fill="#ffffff" style={{ height: '25px' }} />}
          />

          <Switch>
            <Route
              exact
              path={match.url}
              component={(props) => (
                <CondosPresentational
                  data={condos}
                  formattedData={formattedCSVCondos}
                  profile={profile}
                  query={query}
                  total={total}
                  onFilter={onFilter}
                />
              )}
            />

            <Route
              path={`${match.url}/:id`}
              component={(props) => (
                <EonCondosDetail
                  total={total}
                  query={query}
                  onFilter={onFilter}
                  {...props}
                />
              )}
            />
          </Switch>
        </section>
      </div>
    </div>
  );
};

export default withRouter(EonCondos);
